<template>
  <div class="base-details-info">
    <div class="base-details-info__fields">
      <fields-renderer
        :config="fieldsRendererConfig"
        :form-data="formData"
        :fields-config="fieldsConfig"
        :validation-errors="validationErrors"
        :initial-autocompletes="initialAutocompletes"
        :permissions="permissions"
        :edit="edit"
        :create-mode="createMode"
        :update-data="updateData"
        :update-validation-errors="updateValidationErrors"
        :remove-data-property-by-key="removeDataPropertyByKey"
        :replace-property-value-by-key="replacePropertyValueByKey"
        @unlock-fields-on-dbl-click="unlockFieldsOnDblClick"
      />
    </div>

    <!--    The pair is lifted out of the grid's own height calculation - see the
            styles below for why it cannot simply sit in the column.-->
    <div class="base-details-info__side">
      <div class="base-details-info__side-inner">
        <!--      The photo has no form field of its own - the panel writes the picked
                  File straight onto `photo` through updateData, and the tab config
                  sends it to its own endpoint once the base is saved.-->
        <div class="base-details-info__photo">
          <image-upload
            :src="photoSrc"
            :field-key="photoKey"
            :edit="edit"
            :create-mode="createMode"
            :update-data="updateData"
            :update-validation-errors="updateValidationErrors"
            :image-style="PHOTO_STYLE"
            :wrapper-style="PHOTO_WRAPPER_STYLE"
            :pencil-style="PHOTO_PENCIL_STYLE"
            :delete-style="PHOTO_DELETE_STYLE"
            show-delete-button
            contain
            @unlock-fields-on-dbl-click="unlockFieldsOnDblClick"
            @delete-image="clearPhoto"
          />
        </div>

        <div class="base-details-info__map">
          <winter-service-map-panel
            :latitude="formData.latitude"
            :longitude="formData.longitude"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import { isObject } from '@/global/utils/utils'
import FieldsRenderer from '@/global/components/form-layout/FieldsRenderer.vue'
import ImageUpload from '@/global/components/images/ImageUpload.vue'
import WinterServiceMapPanel from '@/modules/road-maintenance-module/winter-service-v2/common/components/WinterServiceMapPanel.vue'
import { BASE_PHOTO_KEY } from '@/modules/road-maintenance-module/winter-service-v2/bases/config/constants'

const PHOTO_STYLE = {
  width: '100%',
  height: '100%'
}
const PHOTO_WRAPPER_STYLE = {
  height: '100%'
}
// The panel is a large photo, not a small avatar - the pencil reads as clipped
// when it sits flush in the rounded corner.
const PHOTO_PENCIL_STYLE = {
  bottom: '8px',
  right: '8px'
}
const PHOTO_DELETE_STYLE = {
  bottom: '8px',
  right: '48px'
}

const props = defineProps({
  // The block's own config from the tab's `tabContent` - its single `rows` entry
  // holds the field list (see useBaseDetails for why it lives there).
  config: {
    type: Object,
    default: () => ({})
  },
  formData: {
    type: Object,
    default: () => ({})
  },
  fieldsConfig: {
    type: Object,
    default: () => ({})
  },
  validationErrors: {
    type: Object,
    default: () => ({})
  },
  initialAutocompletes: {
    type: Object,
    default: () => ({})
  },
  permissions: {
    type: Object,
    default: null
  },
  edit: {
    type: Boolean,
    default: false
  },
  createMode: {
    type: Boolean,
    default: false
  },
  updateData: {
    type: Function,
    default: () => {}
  },
  updateValidationErrors: {
    type: Function,
    default: () => {}
  },
  removeDataPropertyByKey: {
    type: Function,
    default: () => {}
  },
  replacePropertyValueByKey: {
    type: Function,
    default: () => {}
  }
})

const emit = defineEmits(['set-edit'])

const photoKey = BASE_PHOTO_KEY

// FieldsRenderer and ImageUpload only report the double-click; turning the tab into
// edit mode is the block's job, and Content.vue is what listens for `set-edit`. Both
// have already checked themselves - what is left is the tab-level guard.
const unlockFieldsOnDblClick = () => {
  if (props.createMode || (props.permissions && !props.permissions.canEdit)) {
    return
  }

  emit('set-edit', true)
}

/**
 * What the panel is showing: the stored URL, or the File the user just picked.
 *
 * The key is the record's own `photo`, which the backend reports as an object - and
 * Content.vue hands the saved record to the form as it arrives, before this tab's
 * `afterSave` has flattened it. So the object shape has to be read here too, or the
 * panel is handed one for as long as that lasts.
 */
const photoSrc = computed(() => {
  const photo = props.formData[photoKey]

  return isObject(photo) && !(photo instanceof File) ? (photo.url ?? null) : photo
})

// Clearing the panel is all this does. Whether an already stored photo has to be
// dropped on the backend too is decided on save, off the difference between the
// record as it was fetched and the empty key left here - see useBaseDetails.
const clearPhoto = () => {
  props.updateData({
    [photoKey]: null
  })
}

// One field per row, so the form reads as the single column the design shows next
// to the photo and the map.
const fieldsRendererConfig = computed(() => {
  const [fieldsRow] = props.config?.rows || []

  return {
    columnsCount: fieldsRow?.columnsCount || 1,
    fields: fieldsRow?.fields || []
  }
})
</script>

<style scoped>
.base-details-info {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: 24px;
  width: 100%;
  box-sizing: border-box;
  padding: 16px 15px;
}

.base-details-info__fields {
  min-width: 0;
  padding: 16px 20px;
  border: 1px solid #E5E7EB;
  border-radius: 16px;
  box-sizing: border-box;
}

/* The photo and the map are meant to follow the form's height, which means they
   must have no say in it. A grid row sized `auto` takes the tallest of its items,
   and an item that is itself a grid with `1fr` rows still reports its content's
   max-content height however small the tracks are told to be - so an in-flow
   column here would grow to the image's natural height and drag the form's empty
   space along with it. Taking the pair out of flow instead leaves this cell with
   no content of its own: the row is the form's height, `stretch` hands that back
   as this cell's height, and the halves divide it. */
.base-details-info__side {
  min-width: 0;
  position: relative;
}

.base-details-info__side-inner {
  position: absolute;
  inset: 0;
  display: grid;
  grid-template-rows: 1fr 1fr;
  gap: 16px;
}

.base-details-info__photo {
  position: relative;
  min-height: 0;
  border: 1px solid #E5E7EB;
  border-radius: 16px;
  overflow: hidden;
}

.base-details-info__map {
  min-height: 0;
  overflow: hidden;
}

/* Stacked, so there is no form height to follow - the halves carry their own and
   go back in flow to take up room. */
@media (max-width: 960px) {
  .base-details-info {
    grid-template-columns: minmax(0, 1fr);
  }

  .base-details-info__side-inner {
    position: static;
    grid-template-rows: 200px 260px;
  }
}
</style>
